import React, { useMemo } from 'react'
import { DialogTitle, DialogContent, DialogActions, MenuItem, Grid, Button } from '@mui/material'
import { currencyToShortSymbol } from 'services/formatting'
import ResponsiveDialog from 'components/ResponsiveDialog'
import SelectField from 'components/SelectField'
import NumberField from 'components/NumberField'
import InputGroup from 'components/InputGroup'
import InputGroupConnector from 'components/InputGroupConnector'
import SelectCurrencies from 'components/RateField/SelectCurrencies'
import { useForm, useMutationBasedFormSubmitHandlers } from 'hooks/form'
import { graphql } from 'relay-runtime'
import { RateEditDialog_UpdateProfileMutation } from '__generated__/RateEditDialog_UpdateProfileMutation.graphql'
import { RateEditDialog_User$key } from '__generated__/RateEditDialog_User.graphql'
import { useFragment } from 'react-relay'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import FormSubmitButton from 'components/FormSubmitButton'
import Form from 'components/Form'

interface IRateEditDialogProps {
  onClose: () => void
  user: RateEditDialog_User$key
}

const formSchema = z.object({
  jobType: z.enum(['freelance', 'permanent', 'both']),
  freelancerRate: z.number().min(1, { message: 'Rate must be at least 1' }),
  currency: z.string().nonempty('Required'),
  rateMode: z.enum(['hour', 'day', 'week', 'month', 'year'], { message: 'Required' }),
})

type FormFields = z.infer<typeof formSchema>

function RateEditDialog({ onClose, user: userFragmentRef }: IRateEditDialogProps) {
  const user = useFragment(graphql`
    fragment RateEditDialog_User on User {
      profile {
        freelancerRate {
          value
          currency {
            code
          }
        }
        jobTypes
        rateMode
      }
    }
  `, userFragmentRef)

  const defaultValues = useMemo<FormFields>(() => {
    const jobTypes = user?.profile?.jobTypes || []
    let jobType: 'freelance' | 'permanent' | 'both' = 'both'

    if (jobTypes.length === 1) {
      if (jobTypes.includes('freelance')) {
        jobType = 'freelance'
      } else if (jobTypes.includes('permanent')) {
        jobType = 'permanent'
      }
    }

    return {
      jobType,
      freelancerRate: user?.profile?.freelancerRate?.value || 0,
      currency: user?.profile?.freelancerRate?.currency?.code || 'USD',
      rateMode: user?.profile?.rateMode || 'hour',
    }
  }, [user])

  const form = useForm<FormFields, RateEditDialog_UpdateProfileMutation>({
    defaultValues,
    resolver: zodResolver(formSchema),
    submitHandlers: useMutationBasedFormSubmitHandlers(graphql`
      mutation RateEditDialog_UpdateProfileMutation($input: UpdateUserInput!) {
        updateUser(input: $input) {
          user {
            profile {
              jobTypes
              freelancerRate {
                value
                currency {
                  code
                }
              }
              rateMode
            }
          }
        }
      }
    `, (values) => {
      let jobTypes = ['freelance', 'permanent']

      if (values.jobType === 'freelance') {
        jobTypes = ['freelance']
      } else if (values.jobType === 'permanent') {
        jobTypes = ['permanent']
      }

      return {
        input: {
          profile: {
            jobTypes,
            freelancerRate: values.freelancerRate,
            currency: values.currency,
            rateMode: values.rateMode,
          },
        },
      }
    }, {
      successMessage: 'Rate information updated',
      verifySuccess: (result) => {
        return Boolean(result?.updateUser?.user?.profile)
      },
      onSuccess: () => {
        onClose()
      },
    }),
  })

  const jobTypeField = form.useFormField('jobType', 'selection')
  const freelancerRateField = form.useFormField('freelancerRate', 'selection')
  const currencyField = form.useFormField('currency', 'selection')
  const rateModeField = form.useFormField('rateMode', 'selection')

  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="sm">
      <Form form={form}>
        <DialogTitle>
          Edit rate and job type
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SelectField
                {...jobTypeField}
                label="Position types"
                fullWidth
                data-cy="profile-job-type"
              >
                <MenuItem value="freelance" data-cy="profile-job-type-freelance">
                  Freelance positions
                </MenuItem>

                <MenuItem value="permanent" data-cy="profile-job-type-permanent">
                  Permanent positions
                </MenuItem>

                <MenuItem value="both" data-cy="profile-job-type-both">
                  Either permanent or freelance
                </MenuItem>
              </SelectField>
            </Grid>

            <Grid item xs={12}>
              <InputGroup>
                <SelectCurrencies currency={currencyField} />

                <NumberField
                  numDecimals={0}
                  label="Min compensation"
                  {...freelancerRateField}
                  name="freelancer_rate"
                  data-cy="rate-fields-rate-input"
                  startAdornment={currencyToShortSymbol(currencyField.input.value)}
                  fullWidth
                />

                <InputGroupConnector>
                  /
                </InputGroupConnector>

                <SelectField
                  label="Period"
                  {...rateModeField}
                  name="rate_mode"
                  fullWidth
                >
                  <MenuItem value="hour" data-cy="hour">
                    Per hour
                  </MenuItem>

                  <MenuItem value="day" data-cy="day">
                    Per day
                  </MenuItem>

                  <MenuItem value="month" data-cy="month">
                    Per month
                  </MenuItem>

                  <MenuItem value="year" data-cy="year">
                    Per year
                  </MenuItem>
                </SelectField>
              </InputGroup>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">Cancel</Button>
          <FormSubmitButton data-cy="profile-rate-close" />
        </DialogActions>
      </Form>
    </ResponsiveDialog>
  )
}

export default React.memo(RateEditDialog)
